import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FieldPath, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import StepWizard from 'react-step-wizard';
import { toast } from 'react-toastify';
import { FEATURE_FLAGS, SEARCH_PROGRESS_KEY } from 'utils/constants';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import WizardStepsNav from 'v2/common/WizardStepsNav/WizardStepsNav';
import {
  GenericObject,
  SearchFilterEntity,
  Permission,
  SEARCH_FILTER_KEYS,
  CheckDescriptionStatus,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import {
  getPermissionData,
  hasPermission,
  PERMISSION_NAME,
  SEARCHER_PERMISSIONS,
  isFreeSearcher,
} from 'v2/utils/permissions';
import * as yup from 'yup';
import { createSearch, updateSearch } from 'actions';
import styles from './createSearch.module.scss';
import BasicInformationStep from './BasicInformationStep/BasicInformationStep';
import QuestionsStep from './QuestionStep/QuestionsStep';
import FiltersStep from './FiltersStep/FiltersStep';
import ProvidersStep from './ProvidersStep/ProvidersStep';
import { ProvidersStepSchema } from './ProvidersStep/ProvidersStepSchema';
import { FiltersStepSchema } from './FiltersStep/FiltersStepSchema';
import { getBasicInformationSchema } from './BasicInformationStep/CheckDescriptionSchema';
import { QuestionsStepSchema } from './QuestionStep/QuestionsStepSchema';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';
import {
  CreateSearchFormData,
  EditSearchData,
  ExcludeDataVendorFilterForm,
  FilterPost,
  getInitialFormState,
  SearchFilterForm,
  SearchFilterFormPairText,
} from './CreateSearchSchema';
import { createPromptHistoryRelatedEntity } from 'v2/queries/createPromptHistoryRelatedEntity';
import { useMyActiveSearchesCount } from 'v2/queries/searches/useMyActiveSearchesCount';
import { useInvalidateSearchCounts } from 'v2/queries/searches/useInvalidateSearchCounts';
import { useIsInternalSearcher } from 'v2/hooks';

const get_step_names = (isFreeUser = false) => [
  'Basic Information',
  'Providers',
  'Requirements',
  ...(!isFreeUser ? ['Questions'] : []),
];

interface DispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  createSearch: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateSearch: Function;
}

interface OwnProps {
  searchToEdit?: EditSearchData;
}

interface StateProps {
  permissions: { [key: string]: Permission };
}

type CreateSearchProps = StateProps & DispatchProps & OwnProps;

// eslint-disable-next-line complexity
const CreateSearch = ({
  createSearch,
  updateSearch,
  searchToEdit,
  permissions,
}: CreateSearchProps) => {
  const { data: active_searches = 0 } = useMyActiveSearchesCount();
  const [isCheckDescriptionEnabled] = useFeatureFlags([
    FEATURE_FLAGS.CHECK_REQUEST_DESCRIPTION,
  ]);
  const [isFreeConnectEnabled] = useFeatureFlags([FEATURE_FLAGS.FREE_CONNECT]);
  const isFreeUser = isFreeConnectEnabled && isFreeSearcher(permissions);
  const isInternalSearcher = useIsInternalSearcher();
  const STEP_NAMES = get_step_names(isFreeUser);
  const INITIAL_FORM_STATE = getInitialFormState();
  const schemaBasicInformationStep = getBasicInformationSchema(
    isCheckDescriptionEnabled
  );
  const schemasValidators = [
    schemaBasicInformationStep,
    ProvidersStepSchema,
    FiltersStepSchema,
    QuestionsStepSchema,
  ];

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting, isValid },
    getValues,
    trigger,
    setValue,
    watch,
  } = useForm<CreateSearchFormData>({
    defaultValues:
      parseSearchToEdit(searchToEdit) ||
      loadSearchProgress() ||
      INITIAL_FORM_STATE,
    mode: 'onChange',
    resolver: yupResolver(
      schemaBasicInformationStep
        .concat(ProvidersStepSchema)
        .concat(FiltersStepSchema)
        .concat(QuestionsStepSchema)
    ),
    context: { isFreeUser },
  });
  const history = useHistory();
  const check_description_status = watch('check_description_status');
  // The WizardInstance does not have correct type definitions so i have to use Generic object here
  const wizardInstance = useRef<GenericObject>();
  const [currentStep, setCurrentStep] = useState(1);
  const [filters, setFilters] = useState<SearchFilterEntity[]>([]);
  const [
    showSearcherCheckedDescriptionConfirmation,
    setshowSearcherCheckedDescriptionConfirmation,
  ] = useState(false);
  const [showProvidersConfirmation, setshowProvidersConfirmation] =
    useState(false);
  const watchAllFields = watch();
  const permissionList = getPermissionData(permissions, [
    PERMISSION_NAME.CREATE_SEARCH,
    PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
    PERMISSION_NAME.REVIEW_SEARCH_ORG,
    PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
    PERMISSION_NAME.CREATE_SEARCH_OWN,
    PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
    PERMISSION_NAME.CREATE_SEARCH_FREE,
  ]);

  const activePermissions = permissionList.filter(
    (permission: Permission) => permission.subscription_active
  );
  const freeSearcherPermissions = activePermissions.filter(
    (permission: Permission) =>
      [PERMISSION_NAME.CREATE_SEARCH_FREE].includes(permission.permission_name)
  );
  const isfreeUserWithAvailableSearches =
    isFreeUser &&
    freeSearcherPermissions[0]?.permission_quantity > active_searches;
  const searcherPermissions = activePermissions?.filter(
    (permission: Permission) => {
      return SEARCHER_PERMISSIONS.includes(permission.permission_name);
    }
  );

  const saveSearchLocalStorageDebounced = React.useCallback(
    debounce(async (value) => {
      !searchToEdit &&
        localStorage.setItem(SEARCH_PROGRESS_KEY, JSON.stringify(value));
    }, 500),
    []
  );

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    const subscription = watch(saveSearchLocalStorageDebounced);
    return () => subscription.unsubscribe();
  }, [watchAllFields, saveSearchLocalStorageDebounced, watch]);

  useEffect(() => {
    if (wizardInstance.current) {
      wizardInstance.current.onStepChange = ({
        activeStep,
      }: {
        previousStep: number;
        activeStep: number;
      }) => {
        window.scroll(0, 0);
        setTimeout(() => {
          setCurrentStep(activeStep);
        });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardInstance.current]);

  const loadFilters = async () => {
    const response = await restFactory.get<SearchFilterEntity[]>('filters');
    setFilters(response.sort((itemA, itemB) => (itemA.id > itemB.id ? 1 : -1)));
  };

  const getPostSubmitURL = (searchToEdit?: EditSearchData) => {
    if (searchToEdit) {
      return `/requester/request-details/${searchToEdit.id}/detail`;
    }

    if (
      hasPermission(permissions, [
        PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
        PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
      ])
    ) {
      return '/requests/requester/in-review/nav';
    }

    return '/requests/requester/active/nav';
  };

  const invalidateSearchCounts = useInvalidateSearchCounts();

  const onSubmit = async (formValues: CreateSearchFormData) => {
    try {
      if (isValid) {
        const searchDetails = {
          ...formValues,
          free: isFreeUser,
          post_questions: formValues.post_questions
            .map((question) => question.value)
            .filter(Boolean),
          filters: filters.reduce<FilterPost[]>((acc, item) => {
            const filterData = buildFilterData(
              formValues.filters[item.key],
              item
            );
            if (
              filterData === null ||
              (item.key === 'bulk_download' &&
                formValues.filters[item.key] === false)
            ) {
              return acc;
            }

            return [
              ...acc,
              {
                filter: item.id,
                data: filterData,
              },
            ];
          }, []),
        };
        const prompt_history = searchDetails.prompt_history_id;
        delete searchDetails.prompt_history_id;
        let searchId;
        if (searchToEdit) {
          const search = await updateSearch(searchDetails);
          searchId = search?.id;
        } else {
          const search = await createSearch(searchDetails);
          searchId = search?.id;
        }
        if (prompt_history && searchId !== undefined) {
          createPromptHistoryRelatedEntity({
            app_label: 'user',
            model_name: 'searches',
            object_id: searchId,
            prompt_history,
          });
        }

        invalidateSearchCounts();
        toast.success('Request saved');
        if (isFreeUser) {
          history.replace('/success', { from: '/create' });
        } else {
          history.replace(getPostSubmitURL(searchToEdit));
        }
        localStorage.removeItem(SEARCH_PROGRESS_KEY);
      }
    } catch (error) {
      toast.error((error as yup.ValidationError).message);
    }
  };

  const isCurrentStepNextDisabled = () => {
    if (currentStep === 1) {
      if (isCheckDescriptionEnabled) {
        if (isInternalSearcher && !check_description_status) {
          return true;
        }

        if (isFreeUser) {
          if (!check_description_status) return true;
          return check_description_status !== CheckDescriptionStatus.good;
        }
      }
      // we always want to have our next button
      // enabled regardless of the form state
      return false;
    }

    try {
      schemasValidators[currentStep - 1]?.validateSync(getValues(), {
        abortEarly: false,
      });
      return false;
    } catch (error: unknown) {
      return true;
    }
  };

  const getWizardSteps = (isFreeUser: boolean) => {
    return [
      <BasicInformationStep
        control={control}
        register={register}
        errors={errors}
        data={watchAllFields}
        showModal={showSearcherCheckedDescriptionConfirmation}
        onConfirmModal={() => wizardInstance.current?.nextStep()}
        onCancelModal={() =>
          setshowSearcherCheckedDescriptionConfirmation(false)
        }
        key={1}
      />,
      <ProvidersStep
        filters={filters}
        control={control}
        errors={errors}
        getValues={getValues}
        showModal={showProvidersConfirmation}
        onConfirmModal={() => wizardInstance.current?.nextStep()}
        onCancelModal={() => setshowProvidersConfirmation(false)}
        key={2}
      />,
      <FiltersStep
        filters={filters}
        register={register}
        control={control}
        errors={errors}
        getValues={getValues}
        setValue={setValue}
        key={3}
      />,
      ...(!isFreeUser
        ? [
            <QuestionsStep
              control={control}
              register={register}
              errors={errors}
              trigger={trigger}
              getValues={getValues}
              key={4}
            />,
          ]
        : []),
    ];
  };

  const isExcludeProvidersFilterEmpty = () => {
    const filters = getValues('filters');
    const excludeProviders = filters[SEARCH_FILTER_KEYS.exclude_providers];
    if (!excludeProviders) return true;
    return Array.isArray(excludeProviders) && !excludeProviders.length;
  };

  const isExcludeProvidersDRMFilterEmpty = () => {
    const filters = getValues('filters');
    const excludeProvidersDRM = filters[
      SEARCH_FILTER_KEYS.exclude_providers_drm
    ] as ExcludeDataVendorFilterForm;
    return !excludeProvidersDRM?.is_all && !excludeProvidersDRM?.list.length;
  };

  const nextButtonHandler = async () => {
    if (currentStep === 1) {
      if (
        !isFreeUser &&
        check_description_status === CheckDescriptionStatus.bad
      ) {
        setshowSearcherCheckedDescriptionConfirmation(true);
        return;
      }
    }
    if (currentStep === 2) {
      if (
        isFreeUser &&
        isExcludeProvidersDRMFilterEmpty() &&
        isExcludeProvidersFilterEmpty()
      ) {
        setshowProvidersConfirmation(true);
        return;
      }
    }
    try {
      await schemasValidators[currentStep - 1]?.validate(getValues(), {
        abortEarly: false,
      });
      wizardInstance.current?.nextStep();
    } catch (error) {
      trigger(getFieldsWithError(error as yup.ValidationError));
    }
  };

  const handleClickPreviousStep = () => {
    wizardInstance.current?.previousStep();
    setshowProvidersConfirmation(false);
    setshowSearcherCheckedDescriptionConfirmation(false);
  };

  if (
    !activePermissions?.length ||
    (searcherPermissions && searcherPermissions?.length > 1) ||
    (!searchToEdit &&
      !(
        active_searches < searcherPermissions[0]?.permission_quantity ||
        searcherPermissions[0]?.unlimited
      ) &&
      !isfreeUserWithAvailableSearches)
  ) {
    return <Redirect to="/" />;
  }

  return (
    <div className="mt-4 container-xl fix-bg-images">
      <BackgroundImages />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card
          className={classnames(
            'h-100 px-0 mt-5 shadow mx-auto',
            styles.cardRoot
          )}
        >
          <Card.Body>
            <StepWizard
              nav={<WizardStepsNav stepNames={STEP_NAMES} />}
              transitions={{}}
              instance={(instance) => (wizardInstance.current = instance)}
              isLazyMount
            >
              {getWizardSteps(isFreeUser)}
            </StepWizard>
          </Card.Body>
          <Card.Footer className={'bg-white d-flex justify-content-between'}>
            {searchToEdit && (
              <label
                className={classnames('px-3', styles.cancel)}
                onClick={() => history.goBack()}
              >
                Cancel
              </label>
            )}
            {currentStep > 1 ? (
              <Button
                className={searchToEdit ? 'ml-auto mr-3' : ''}
                onClick={handleClickPreviousStep}
              >
                Previous
              </Button>
            ) : null}

            {currentStep < STEP_NAMES.length ? (
              <Button
                disabled={isCurrentStepNextDisabled()}
                className={!searchToEdit ? 'ml-auto' : ''}
                type="button"
                data-cy="CreateRequest:next-button"
                onClick={nextButtonHandler}
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                className={!searchToEdit ? 'ml-auto' : ''}
                disabled={isSubmitting}
                data-cy="CreateRequest:submit-button"
              >
                Submit
              </Button>
            )}
          </Card.Footer>
        </Card>
      </form>
    </div>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    permissions: state.permissions.permissions,
  };
};

export default connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, {
  createSearch,
  updateSearch,
})(CreateSearch);

// eslint-disable-next-line complexity
function buildFilterData(
  filter: SearchFilterForm | undefined,
  filterData: SearchFilterEntity
): FilterPost['data'] | null {
  if (typeof filter === 'boolean') {
    return { filter_value: filter };
  }

  if (filterData.key === 'exclude_providers_drm') {
    const filterValue = filter as ExcludeDataVendorFilterForm;
    if (filterValue.list && filterValue.list.length) {
      return { filter_value: filterValue };
    }
  }
  if ((filter && typeof filter === 'string') || typeof filter === 'number') {
    const key = filterData.key === 'granularity' ? 'name' : 'id';
    const filterOption = filterData.options.find(
      (item) => String(item[key]) === String(filter)
    );

    return {
      filter_value: filterOption?.name || '',
    };
  }
  if (Array.isArray(filter) && filter.length) {
    return {
      filter_value: filter.map((item) => {
        if ('name' in item) {
          return item.name;
        }
        return item.value;
      }),
    };
  }
  const filterTyped = filter as SearchFilterFormPairText;
  if (filterTyped?.unit && filterTyped?.value) {
    return {
      filter_value: +filterTyped.value,
      filter_units: filterTyped.unit,
    };
  }
  return null;
}

function getFieldsWithError(error: yup.ValidationError) {
  const fieldsWithError = error?.inner.map((item) => item.path);
  return fieldsWithError as
    | FieldPath<CreateSearchFormData>
    | FieldPath<CreateSearchFormData>[];
}

function loadSearchProgress() {
  const searchData = localStorage.getItem(SEARCH_PROGRESS_KEY);
  if (searchData) {
    return JSON.parse(searchData);
  }
  return null;
}

function parseSearchToEdit(searchToEdit?: CreateSearchFormData) {
  if (!searchToEdit) {
    return null;
  }
  const INITIAL_FORM_STATE = getInitialFormState();
  const parsedSearchToEdit = searchToEdit;
  for (const filter in INITIAL_FORM_STATE.filters) {
    const filterKey = filter as SEARCH_FILTER_KEYS;
    parsedSearchToEdit.filters[filterKey] =
      parsedSearchToEdit.filters[filterKey] ||
      INITIAL_FORM_STATE.filters[filterKey];
  }

  return parsedSearchToEdit;
}
